'use client';

import SendIcon from '@/public/assets/send-message.svg';
import { Stop } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import type { ChatRequestOptions, CreateMessage, Message } from 'ai';
import { useSearchParams } from 'next/navigation';
import { type ChangeEvent, memo } from 'react';
import { type ChangeEventHandler, type FormEvent, type KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../button';
export interface ChatInputProps {
  input: string;
  setInput: (input: string) => void;
  handleInputChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  handleSubmit: (e: FormEvent<HTMLFormElement>, data: Record<string, unknown> | undefined) => Promise<void>;
  isDisabled?: boolean;
  replicaName: string;
  replicaAnswering: boolean;
  inputPrompt?: string;
  onStop: () => void;
  appendMessage: (message: Message | CreateMessage, chatRequestOptions?: ChatRequestOptions) => Promise<string | null | undefined> | null;
  classNameInput?: string;
  primaryBgColor?: string;
}
export default memo(function ChatInput({
  input,
  handleInputChange: _handleInputChange,
  handleSubmit,
  isDisabled = false,
  replicaName,
  replicaAnswering,
  inputPrompt = 'Message',
  onStop,
  classNameInput = ''
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isBeforeFirstResponseStart = useRef(true);
  const searchParams = useSearchParams();
  const isPreview = searchParams.get('isPreview') === 'true';
  const autoFocus = !isPreview;
  const {
    mutate: submitMessage,
    isPending
  } = useMutation({
    mutationFn: async (e: FormEvent<HTMLFormElement>) => {
      await handleSubmit(e, undefined);
    }
  });
  function handleInputChange(e: ChangeEvent<HTMLTextAreaElement>) {
    if (replicaAnswering) {
      e.preventDefault();
      return;
    }
    _handleInputChange(e);
  }
  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 2}px`;
    }
  };
  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sendMessageDisabled) {
      return;
    }
    submitMessage(e);
    const currentTextAreaRef = textareaRef.current;
    if (!currentTextAreaRef) return;
    currentTextAreaRef.style.overflowY = '';
    currentTextAreaRef.style.height = '';
    textareaRef.current?.focus();
  };
  const handleTextareaInput = () => {
    adjustHeight();
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      onSubmit(e);
    }
  };
  const focusTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);
  useEffect(() => {
    if (replicaAnswering) {
      if (isBeforeFirstResponseStart.current) {
        isBeforeFirstResponseStart.current = false;
        return;
      }
    }
    if (!isDisabled && autoFocus) {
      focusTextarea();
    }
  }, [isDisabled, replicaAnswering, focusTextarea, autoFocus]);

  // Add message listener for focus requests from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.messageType === 'focus_input') {
        focusTextarea();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [focusTextarea]);
  const hasText = !!input.trim().length;
  const sendMessageDisabled = isDisabled || !hasText || replicaAnswering || isPending;
  const textareaPlaceholder = `${inputPrompt} ${replicaName}...`;
  return <div>
      <form onSubmit={onSubmit} onKeyDown={handleKeyDown}>
        <fieldset>
          <div className={twMerge('flex min-h-11 w-full items-center border-t border-foreground/20 bg-background brightness-110 p-2 py-3 pl-4', classNameInput)}>
            <textarea ref={textareaRef} className={twMerge('text-[16px] h-auto w-full max-h-[115px] resize-none overflow-y-auto border-0 bg-transparent p-0 text-foreground placeholder:text-foreground/50 focus-within:outline-none focus:ring-0', (isDisabled || replicaAnswering) && 'opacity-40', 'scrollbar-w-2 scrollbar-thumb-foreground/50 scrollbar-track-transparent hover:scrollbar-thumb-foreground/80')} style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'color-mix(in srgb, var(--sensay-widget-foreground-color) 50%, transparent) transparent'
          }} value={input} onChange={handleInputChange} onInput={handleTextareaInput} placeholder={textareaPlaceholder} rows={1} autoFocus={autoFocus} />

            {!replicaAnswering && <Button variant="ghost" size="icon" type="submit" className={twMerge('self-start', sendMessageDisabled ? 'text-foreground/50' : 'text-primary')} disabled={sendMessageDisabled}>
                <div className="flex items-center justify-center w-9 h-9">
                  <SendIcon className={'w-[23px] fill-current'} />
                </div>
              </Button>}

            {replicaAnswering && <Button size="icon" type="button" variant={'ghost'} className="text-foreground" onClick={onStop}>
                <div className="flex items-center justify-center w-9 h-9">
                  <Stop weight="fill" className={'w-[23px] h-[23px] fill-current'} />
                </div>
              </Button>}
          </div>
        </fieldset>
      </form>
    </div>;
});